import React from 'react';
import styled, { css } from 'styled-components';
import {
  breakpointSizes,
  minBreakpointQuery,
  maxBreakpointQuery,
  standardColours,
  brandColours,
  brandFonts,
  fontWeights,
  fontSize,
  fluidFontSize,
  visuallyHidden,
} from '../styles';
import { Container, Button, Svg } from './ui';
import VoucherButton from './VoucherButton';
import WishlistToggle from './WishlistToggle';
import TrustpilotWidget from './TrustpilotWidget';
import { formatPrice } from '../utils';
import shareIcon from '../images/share-icon.svg';

const StyledHeader = styled.header`
  ${maxBreakpointQuery.smedium`
    position: relative;
    padding-top: 55px;
  `}

  ${minBreakpointQuery.smedium`
    padding-top: 35px;
  `}

  ${minBreakpointQuery.large`
    padding-top: 45px;
  `}
`;

const StyledInner = styled.div`
  ${minBreakpointQuery.smedium`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 20px;
  `}
`;

const StyledGroup = styled.div``;

const StyledHeading = styled.h1`
  ${brandFonts.secondary};
  ${fluidFontSize(
    '38px',
    '60px',
    breakpointSizes.tiny,
    breakpointSizes.xxlarge,
  )};
  line-height: 1;
`;

const StyledAddress = styled.p`
  margin-top: 10px;
  line-height: 1.4;
`;

const StyledPrice = styled.p`
  margin-top: 10px;

  span {
    ${fontSize(28)};

    strong {
      font-weight: ${fontWeights.medium};
    }
  }
`;

const StyledJenniferYoungInfo = styled.p`
  margin-top: 12px;
  color: ${brandColours.tertiary};
  font-weight: ${fontWeights.bold};
  ${fontSize(13)};
`;

const StyledRating = styled.p`
  ${fontSize(18)};

  ${maxBreakpointQuery.smedium`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    padding: 5px 20px;
    color: ${standardColours.white};
    text-align: center;
    background-color: ${brandColours.primary};
  `}

  ${minBreakpointQuery.smedium`
    text-align: right;
  `}

  ${({ altTheme }) => {
    if (altTheme) {
      return css`
        color: ${brandColours.septenary};
      `;
    }
  }}
`;

const StyledButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 15px;

  ${maxBreakpointQuery.large`
    flex-wrap: wrap;
  `}

  ${maxBreakpointQuery.smedium`
    justify-content: space-between;
  `}

  ${minBreakpointQuery.tsmall`
    gap: 15px;
  `}

  ${minBreakpointQuery.smedium`
    gap: 20px;
    margin-top: 20px;
    flex-direction: row-reverse;
  `}

  ${minBreakpointQuery.large`
    gap: 25px;
    margin-top: 25px;
  `}
`;

const StyledButton = styled(Button)`
  white-space: nowrap;
`;

const StyledIconButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  ${minBreakpointQuery.large`
    gap: 15px;
  `}
`;

const StyledShare = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0;
  height: 42px;
  width: 42px;
  border: 1px solid
    ${({ altTheme }) =>
      altTheme ? standardColours.white : brandColours.primary};
  border-radius: 50%;

  svg {
    height: 20px;
    width: 20px;
    fill: ${({ altTheme }) =>
      altTheme ? standardColours.white : brandColours.primary};
  }

  span {
    ${visuallyHidden()};
  }
`;

const StyledTrustpilotWidgetWrapper = styled.div`
  margin-top: 15px;

  iframe {
    ${minBreakpointQuery.smedium`
      margin-left: auto;
    `}
  }
`;

const SpaHeader = ({
  heading,
  address,
  minPrice,
  higherPrice,
  isPricePerPerson,
  isJenniferYoung,
  rating,
  shareData,
  wishlistSpa,
  altTheme,
  hasPackages,
  giftVouchers,
  activateSpaVouchersFilter,
}) => {
  const handleScrollToPackages = () => {
    const element = document.getElementById('spa-packages');

    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <StyledHeader id="spa-introduction">
      <Container>
        <StyledInner>
          <StyledGroup>
            <StyledHeading>{heading}</StyledHeading>
            <StyledAddress>{address}</StyledAddress>
            <StyledPrice>
              {minPrice ? (
                <>
                  From{' '}
                  <span>
                    {higherPrice && (
                      <>
                        <s>{formatPrice(higherPrice)}</s>{' '}
                      </>
                    )}
                    <strong>{formatPrice(minPrice)}</strong>{' '}
                  </span>{' '}
                  per {isPricePerPerson ? 'person' : 'package'}
                </>
              ) : (
                <>
                  {giftVouchers && (
                    <>
                      Gift Vouchers from <span>{formatPrice(25)}</span>
                    </>
                  )}
                </>
              )}
            </StyledPrice>
            {isJenniferYoung && (
              <StyledJenniferYoungInfo>
                Suitable despite cancer
              </StyledJenniferYoungInfo>
            )}
          </StyledGroup>
          <StyledGroup>
            {rating > 0 && (
              <StyledRating altTheme={altTheme}>
                Our customer rating: {rating}/5
              </StyledRating>
            )}
            <StyledButtons>
              {!!hasPackages && (
                <StyledButton alt={true} onClick={handleScrollToPackages}>
                  View Packages &amp; Book
                </StyledButton>
              )}
              <VoucherButton
                altTheme={altTheme}
                onClick={() => {
                  activateSpaVouchersFilter();
                  document
                    .getElementById('spa-packages')
                    .scrollIntoView({ behavior: 'smooth' });
                }}
              />
              <StyledIconButtons>
                {typeof window !== 'undefined' &&
                  navigator &&
                  navigator.share && (
                    <StyledShare
                      altTheme={altTheme}
                      onClick={() => navigator.share(shareData)}
                    >
                      <Svg image={shareIcon} />
                      <span>Share</span>
                    </StyledShare>
                  )}
                <WishlistToggle
                  spa={wishlistSpa}
                  alt={!altTheme}
                  altTheme={altTheme}
                />
              </StyledIconButtons>
            </StyledButtons>
            <StyledTrustpilotWidgetWrapper>
              <TrustpilotWidget type="mini" isTest={true} />
            </StyledTrustpilotWidgetWrapper>
          </StyledGroup>
        </StyledInner>
      </Container>
    </StyledHeader>
  );
};
export default SpaHeader;
